import { create } from 'zustand';
import type { QuestionDto, QuestionStatus } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { settleModerated } from '@/lib/queue-rows';
import { useWorkspaceStore } from './workspace.store';

/**
 * Customers' questions about products (R20, ADR-047).
 *
 * Nothing is public until it is answered: answering publishes a question with
 * the store's answer, and the asker is mailed on the first one. Hiding takes a
 * question down; publishing again needs the answer it already has.
 */

export interface QuestionFilters {
  search: string;
  statuses: QuestionStatus[];
  /** One product at a time. */
  productId: string;
}

export const EMPTY_QUESTION_FILTERS: QuestionFilters = { search: '', statuses: [], productId: '' };

interface QuestionsState {
  items: QuestionDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: QuestionFilters;
  total: number | null;
  /** The filtered product's name, for its chip. Null while unknown. */
  productName: string | null;
  /** Questions with a change in flight: each card waits on its own, not on its neighbours. */
  busyIds: string[];

  load: (filters?: Partial<QuestionFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  /** Writes or rewrites the answer, which publishes the question. */
  answer: (id: string, body: string) => Promise<boolean>;
  moderate: (id: string, status: Extract<QuestionStatus, 'PUBLISHED' | 'REJECTED'>) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useQuestionsStore = create<QuestionsState>((set, get) => {
  /** A change to one question: only its card waits, and the menu's count follows. */
  async function change(id: string, call: () => Promise<QuestionDto>, fallback: string): Promise<boolean> {
    set((state) => ({ busyIds: [...state.busyIds, id], error: null }));
    try {
      const updated = await withSession(call);
      // A question that leaves the view's statuses leaves the list; elsewhere it updates in place.
      set((state) => settleModerated(state.items, state.total, state.filters.statuses, updated));
      void useWorkspaceStore.getState().refreshAttention();
      return true;
    } catch (error) {
      set({ error: errorMessage(error, fallback) });
      return false;
    } finally {
      set((state) => ({ busyIds: state.busyIds.filter((entry) => entry !== id) }));
    }
  }

  return {
    items: [],
    nextCursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    // Opens on the questions waiting for an answer.
    filters: { ...EMPTY_QUESTION_FILTERS, statuses: ['PENDING'] },
    total: null,
    productName: null,
    busyIds: [],

    async load(filters) {
      const previous = get();
      const next = { ...previous.filters, ...filters };
      set({ loading: true, error: null, filters: next });
      try {
        const page = await withSession(() => adminApi.questions.list(toQuery(next, PAGE_SIZE)));
        // Every row of a product's questions carries its name; only an empty
        // list has to ask for it.
        const known =
          page.items[0]?.product.name ??
          (previous.filters.productId === next.productId ? previous.productName : null);
        set({
          items: page.items,
          nextCursor: page.meta.nextCursor,
          total: page.meta.total ?? null,
          loading: false,
          productName: next.productId ? known : null,
        });

        if (next.productId && !known) {
          const name = await productNameOf(next.productId);
          if (get().filters.productId === next.productId) set({ productName: name });
        }
      } catch (error) {
        set({ loading: false, error: errorMessage(error, 'Could not load the questions.') });
      }
    },

    async loadMore() {
      const { nextCursor, loadingMore, filters, items } = get();
      if (!nextCursor || loadingMore) return;
      set({ loadingMore: true });
      try {
        const page = await withSession(() => adminApi.questions.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }));
        set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
      } catch (error) {
        set({ loadingMore: false, error: errorMessage(error, 'Could not load more questions.') });
      }
    },

    answer: (id, body) => change(id, () => adminApi.questions.answer(id, body), 'Could not save the answer.'),

    moderate: (id, status) =>
      change(id, () => adminApi.questions.moderate(id, status), 'Could not change the question.'),
  };
});

/** A product's name by id, for the filter chip when no row on screen carries it. Null when it cannot be read. */
async function productNameOf(id: string): Promise<string | null> {
  try {
    return (await withSession(() => adminApi.catalog.product(id))).name;
  } catch {
    return null;
  }
}

function toQuery(filters: QuestionFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    productId: filters.productId || undefined,
  };
}
